import useTranslator from "@pma/hooks/use-translator";
import { Users } from "lucide-react";
import axios from "axios";
import { FINANCIAL_SERVER_URL, SET_USER_PROJECT_ROLE_ENDPOINT } from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@pma/hooks/use-toast";
import { DictType } from "@pma/types";
import { useAuth } from "@pma/stores/auth-store";
import { Avatar, AvatarImage } from "@components/common/avatar";
import { cn } from "@pma/utils/helpers";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover";
import { Separator } from "@components/common/separator";

interface Props {
  revalidate: CallableFunction;
  role: DictType;
  projectId?: string;
}

const AssignProjectRole = ({ revalidate, role, projectId }: Props) => {
  const { translate } = useTranslator();
  const queryClient = useQueryClient()

  const { authConfig } = useAuth();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const organizationUsers = authConfig?.organizationUsers || [];

  const assignRole = async (userId: string) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + SET_USER_PROJECT_ROLE_ENDPOINT,
      {
        user_id: userId,
        projects_id: projectId,
        role_type_id: role.id,
      },

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const assignRoleMutation = useMutation({
    mutationKey: ["assign-role"],
    mutationFn: assignRole,
    onSuccess: () => {
      revalidate();
      queryClient.invalidateQueries({ queryKey: ["user-role-details"]})
      queryClient.invalidateQueries({ queryKey: ["organization-role-details"]})
      queryClient.invalidateQueries({ queryKey: ["get-project-wise-contractors-list"]})
      toast({
        title: translate("Success"),
        description: translate("Role assigned"),
        variant: "success",
      });
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to assign role"),
        variant: "error",
      });
    },
  });
  return (
    <Popover>
      <PopoverTrigger className='border rounded-md p-1.5 gap-1 flex justify-between items-center w-fit'>
        <Users className='h-4 w-4 mr-1' />
        {translate("Assign")}
      </PopoverTrigger>

      <PopoverContent className='p-0'>
        <Command>
          <CommandInput placeholder={translate("Search for user") + "..."} />
          <CommandList>
            <CommandEmpty>{translate("No users found")}.</CommandEmpty>
            <CommandGroup className='w-full'>
              {organizationUsers?.map((user: DictType) => (
                <CommandItem
                  value={user?.name}
                  defaultValue={user?.name}
                  onSelect={() => assignRoleMutation.mutate(user?.id)}
                  className={cn("my-1 capitalize gap-1 w-full flex-col")}
                  key={user.id}
                >
                  <PopoverClose className='w-[100%] gap-1 flex-1 flex item-center'>
                    <Avatar className='relative -ml-2 h-5 w-5'>
                      <AvatarImage
                        src={
                          user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                        }
                      />
                    </Avatar>{" "}
                    <span>{user?.name || user?.email}</span>
                  </PopoverClose>
                  <Separator />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AssignProjectRole;
